import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BaseDto } from '@models/base/baseDto.model';
import { EstadosContratoService } from '@services/estados-contratos/estados-contrato.service';
import { DetallePeriodoContratoDeuda } from '@models/periodos-contrato/detallePeriodoContratoDeuda.modal';
import { ReporteDeudaPeriodoContratoRequest } from '@models/periodos-contrato/reporteDeudaPeriodoContratoRequest.model';
import { ReporteDeudaPeriodoContratoResponse } from '@models/periodos-contrato/reporteDeudaPeriodoContratoResponse.model';
import { PeriodosContratoService } from '@services/periodos-contrato/periodos-contrato.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { ChartConfiguration, ChartOptions,ChartType } from 'chart.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { truncateSync } from 'fs';
import { Color, Label, SingleDataSet, ThemeService } from 'ng2-charts';

@Component({
  selector: 'app-reporte-deuda-periodo-contrato',
  templateUrl: './reporte-deuda-periodo-contrato.component.html',
  styleUrls: ['./reporte-deuda-periodo-contrato.component.css'],
})
export class ReporteDeudaPeriodoContratoComponent {

  form!: FormGroup;
  selectedTab = 0;
  reporteData: ReporteDeudaPeriodoContratoResponse | null = null;
  dataSource = new MatTableDataSource<DetallePeriodoContratoDeuda>();
  displayedColumns: string[] = [
    'numeroContrato',
    // 'nombreInquilino',
    // 'nombrePropietario',
    'propiedad',
    'totalPeriodos',
    'periodosEnMora',
    'diasAtraso',
    'montoEnMora',
    'deudaInquilino',
    'porcentajePagadoInquilino',
    'deudaPropietario',
    'porcentajeLiquidadoPropietario'
  ];

  contratoSeleccionado: BaseDto | null = null;

  // Datos para gráficos Chart.js
  // Gráfico de torta - Inquilinos
  pieChartInquilinosData: any = {
    datasets: [{
      data: [],
      backgroundColor: ['#4CAF50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };
  pieChartInquilinosLabels: string[] = ['Pagado', 'Adeudado'];

  // Gráfico de torta - Propietarios
  pieChartPropietariosData: any = {
    datasets: [{
      data: [],
      backgroundColor: ['#2196F3', '#FF9800'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };
  pieChartPropietariosLabels: string[] = ['Liquidado', 'Por Liquidar'];

  // Gráfico de barras comparativo
  barChartData: any = {
    datasets: [
      {
        label: 'Pagado/Liquidado',
        data: [],
        backgroundColor: '#4CAF50',
        borderColor: '#4CAF50',
        borderWidth: 1
      },
      {
        label: 'Adeudado/Por Liquidar',
        data: [],
        backgroundColor: '#f44336',
        borderColor: '#f44336',
        borderWidth: 1
      }
    ]
  };
  barChartLabels: string[] = ['Inquilinos', 'Propietarios'];

  // Opciones para gráficos
  pieChartType: ChartType = 'pie';
  pieChartLegend = true;
  pieChartPlugins = [];
  pieChartOptions: ChartConfiguration['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          return `${label}: ${context.raw.toFixed(1)}%`;
        }
      }
    }
  }
};

  barChartType: ChartType = 'bar';
  barChartLegend = true;
  barChartPlugins = [];
  barChartOptions: ChartConfiguration['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          return `${context.dataset.label}: ${context.raw.toFixed(1)}%`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value: any) => `${value}%`
      }
    }
  } as any
};

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  contratoDetailDto!: BaseDto;

  get f() { return this.form.controls; }

  constructor(
    private fb: FormBuilder,
    private periodosContratoService: PeriodosContratoService,
    private estadosContratoService: EstadosContratoService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.getEstadosContrato();

    // El selector arranca deshabilitado porque el check viene prendido
    this.onCambiaSoloActivos();
  }

  trackByItems(index: number, item: any): any { return index; }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private createForm(): void {
    this.form = this.fb.group({
      soloContratosActivos: [true],
      periodoMesDesde: [null],
      periodoAnioDesde: [null],
      periodoMesHasta: [null],
      periodoAnioHasta: [null],
      idsEstadoContrato: [[]],
      minPeriodosEnMora: [null],
      minDiasAtraso: [null],
      minMontoEnMora: [null],
      // El usuario piensa en cuánto DEBEN, no en cuánto pagaron. El control se
      // llama como la pregunta que se hace en pantalla; la conversión al
      // criterio que entiende el back se hace al armar el request.
      minPorcentajeAdeudado: [null]
    });
  }

  /** Estados de contrato para el selector. Salen de la base, no hardcodeados. */
  lstEstadosContrato: BaseDto[] = [];

  getEstadosContrato() {
    this.estadosContratoService.getAll().subscribe(
      data => this.lstEstadosContrato = isFalsy(data) ? [] : data,
      error => this.snackBar.showError(error, "Error")
    );
  }

  /**
   * El check "Solo contratos activos" y el selector de estados dicen lo mismo
   * de dos formas. Al prender el check se limpia el selector para que no quede
   * mostrando una selección que después se ignora.
   */
  onCambiaSoloActivos() {
    if (this.f.soloContratosActivos.value)
      this.form.controls['idsEstadoContrato'].setValue([]);

    this.f.soloContratosActivos.value
      ? this.form.controls['idsEstadoContrato'].disable()
      : this.form.controls['idsEstadoContrato'].enable();
  }

  /** Un input numérico vacío llega como "" o null: se manda null en los dos casos. */
  private normalizarUmbral(valor: any): number | null {
    if (valor === null || valor === undefined || valor === '')
      return null;

    const numero = Number(valor);

    return isNaN(numero) ? null : numero;
  }

  /**
   * Pasa el "% adeudado mínimo" que carga el usuario al "% pagado máximo" que
   * espera el back. Fuera del rango 0-100 no se manda nada: un valor absurdo
   * filtrando en silencio es peor que no filtrar.
   */
  private convertirAdeudadoAPagado(valor: any): number | null {
    const adeudado = this.normalizarUmbral(valor)

    if (adeudado === null)
      return null

    if (adeudado < 0 || adeudado > 100)
      return null

    return 100 - adeudado
  }

  hayFiltrosDeMora(): boolean {
    return this.normalizarUmbral(this.f.minPeriodosEnMora.value) !== null
        || this.normalizarUmbral(this.f.minDiasAtraso.value) !== null
        || this.normalizarUmbral(this.f.minMontoEnMora.value) !== null
        || this.normalizarUmbral(this.f.minPorcentajeAdeudado.value) !== null;
  }

  limpiarFiltrosDeMora(): void {
    this.form.patchValue({
      minPeriodosEnMora: null,
      minDiasAtraso: null,
      minMontoEnMora: null,
      minPorcentajeAdeudado: null
    });
  }

  /**
   * Gravedad de la mora, para pintar la fila. Los cortes son los habituales de
   * cobranza: hasta 30 días se recupera solo, después hay que salir a buscarlo.
   */
  nivelMora(detalle: DetallePeriodoContratoDeuda): string {
    if (isFalsy(detalle) || detalle.diasAtraso <= 0) return 'aldia';
    if (detalle.diasAtraso <= 30) return 'leve';
    if (detalle.diasAtraso <= 90) return 'media';

    return 'grave';
  }

  onContratoSelected(contrato: BaseDto | null): void {
    this.contratoSeleccionado = contrato;
  }

  generarReporte(): void {
    this.spinner.show('spReporteDeuda');

    const request: ReporteDeudaPeriodoContratoRequest = {
      soloContratosActivos: this.f.soloContratosActivos.value,
      idContrato: this.contratoSeleccionado?.id,
      periodoMesDesde: this.f.periodoMesDesde.value,
      periodoAnioDesde: this.f.periodoAnioDesde.value,
      periodoMesHasta: this.f.periodoMesHasta.value,
      periodoAnioHasta: this.f.periodoAnioHasta.value,

      // El check es un atajo del caso más común. Si está prendido manda él y
      // los estados elegidos se ignoran, para no mandar dos criterios en conflicto.
      idsEstadoContrato: this.f.soloContratosActivos.value ? [] : (this.f.idsEstadoContrato.value ?? []),

      // Un umbral vacío no se manda: null significa "sin límite" en el back.
      minPeriodosEnMora: this.normalizarUmbral(this.f.minPeriodosEnMora.value),
      minDiasAtraso: this.normalizarUmbral(this.f.minDiasAtraso.value),
      minMontoEnMora: this.normalizarUmbral(this.f.minMontoEnMora.value),

      /* "Deben al menos el 30%" es lo mismo que "pagaron como mucho el 70%".
         Se convierte acá y el back queda igual: no tiene sentido tocar un
         endpoint por un cambio de redacción. */
      maxPorcentajePagado: this.convertirAdeudadoAPagado(this.f.minPorcentajeAdeudado.value)
    };

    this.periodosContratoService.generarReporteDeudaPeriodoContrato(request)
      .subscribe({
        next: (response) => {
          this.reporteData = response;
          this.dataSource.data = response.detalleContratos;
          this.prepararDatosGraficos();
          this.spinner.hide('spReporteDeuda');
        },
        error: (error) => {
          this.spinner.hide('spReporteDeuda');
          this.snackBar.showError(error, 'Error al generar el reporte');
        }
      });
  }

  selectContrato(baseDto: BaseDto | null) {
    if (!isFalsy(baseDto))
      this.contratoDetailDto = baseDto
  }

  private prepararDatosGraficos(): void {
  if (!this.reporteData) return;

  // Totales
  const totalInquilinos = this.reporteData.resumenInquilinos.montoPagado + this.reporteData.resumenInquilinos.montoAdeudado;
  const totalPropietarios = this.reporteData.resumenPropietarios.montoPagado + this.reporteData.resumenPropietarios.montoAdeudado;

  // Datos en % para gráfico de torta - Inquilinos
  this.pieChartInquilinosData = {
    datasets: [{
      data: [
        totalInquilinos ? ((this.reporteData.resumenInquilinos.montoPagado / totalInquilinos) * 100).toFixed(2)  : 0,
        totalInquilinos ? ((this.reporteData.resumenInquilinos.montoAdeudado / totalInquilinos) * 100).toFixed(2)  : 0
      ],
      backgroundColor: ['#4CAF50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Datos en % para gráfico de torta - Propietarios
  this.pieChartPropietariosData = {
    datasets: [{
      data: [
        totalPropietarios ? ((this.reporteData.resumenPropietarios.montoPagado / totalPropietarios) * 100).toFixed(2)  : 0,
        totalPropietarios ? ((this.reporteData.resumenPropietarios.montoAdeudado / totalPropietarios) * 100).toFixed(2)  : 0
      ],
      backgroundColor: ['#2196F3', '#FF9800'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Gráfico de barras comparativo en %
  this.barChartData = {
    datasets: [
      {
        label: 'Pagado/Liquidado (%)',
        data: [
          totalInquilinos ? ((this.reporteData.resumenInquilinos.montoPagado / totalInquilinos) * 100).toFixed(2)  : 0,
          totalPropietarios ? ((this.reporteData.resumenPropietarios.montoPagado / totalPropietarios) * 100).toFixed(2)  : 0
        ],
        backgroundColor: '#4CAF50',
        borderColor: '#4CAF50',
        borderWidth: 1
      },
      {
        label: 'Adeudado/Por Liquidar (%)',
        data: [
          totalInquilinos ? ((this.reporteData.resumenInquilinos.montoAdeudado / totalInquilinos) * 100).toFixed(2)  : 0,
          totalPropietarios ? ((this.reporteData.resumenPropietarios.montoAdeudado / totalPropietarios) * 100).toFixed(2)  : 0
        ],
        backgroundColor: '#f44336',
        borderColor: '#f44336',
        borderWidth: 1
      }
    ]
  };
}

  limpiarFiltros(): void {
    this.form.reset({
      soloContratosActivos: true,
      periodoMesDesde: null,
      periodoAnioDesde: null,
      periodoMesHasta: null,
      periodoAnioHasta: null
    });
    this.contratoSeleccionado = null;
  }

  exportarExcel(): void {
    // Implementar exportación a Excel
    this.snackBar.showSuccess('Funcionalidad de exportación en desarrollo', 'Info');
  }

  // Helpers para el template
  formatearMoneda(valor: number): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor);
  }

  formatearPorcentaje(valor: number): string {
    return `${valor.toFixed(2)}%`;
  }

  getEstadoContrato(activo: boolean): string {
    return activo ? 'Activo' : 'Inactivo';
  }

  getClassEstadoContrato(activo: boolean): string {
    return activo ? 'estado-activo' : 'estado-inactivo';
  }

  isFalsy(valor: any): boolean {
    return isFalsy(valor);
  }

  trackByContrato(index: number, item: DetallePeriodoContratoDeuda): number {
    return item.idContrato;
  }

}
